import { Scrollable } from '@affine/component';
import { PageDetailLoading } from '@affine/component/page-detail-skeleton';
import { AIAppEvents, type AIChatParams } from '@affine/core/blocksuite/ai';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { EditorOutlineViewer } from '@affine/core/blocksuite/outline-viewer';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { useGuard } from '@affine/core/components/guard';
import { PageNotFound } from '@affine/core/desktop/pages/404';
import { EditorService } from '@affine/core/modules/editor';
import { isNewTabTrigger } from '@affine/core/utils/event';
import { DebugLogger } from '@affine/debug';
import { DisposableGroup } from '@blocksuite/affine/global/disposable';
import { Bound } from '@blocksuite/affine/global/gfx';
import { RefNodeSlotsProvider } from '@blocksuite/affine/inlines/reference';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import {
  FrameworkScope,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { lazy, Suspense, useCallback, useEffect } from 'react';
import type { Subscription } from 'rxjs';

import { WorkbenchService } from '../../../workbench/services/workbench';
import type { DocReferenceInfo } from '../../entities/peek-view';
import { PeekViewService } from '../../services/peek-view';
import { useEditor } from '../utils';
import * as styles from './doc-peek-view.css';

const logger = new DebugLogger('doc-peek-view');

// Lazy load BlockSuiteEditor to break circular dependency
const BlockSuiteEditor = lazy(() =>
  import('@affine/core/blocksuite/block-suite-editor').then(module => ({
    default: module.BlockSuiteEditor,
  }))
);

function fitViewport(
  editor: AffineEditorContainer,
  xywh?: `[${number},${number},${number},${number}]`
) {
  try {
    if (!editor.host) {
      throw new Error('editor host is not ready');
    }

    const gfx = editor.host.std.get(GfxControllerIdentifier);
    const viewport = gfx.viewport;
    viewport.onResize();

    if (xywh) {
      const newViewport = {
        xywh: xywh,
        padding: [60, 20, 20, 20] as [number, number, number, number],
      };
      viewport.setViewportByBound(
        Bound.deserialize(newViewport.xywh),
        newViewport.padding,
        false
      );
    } else {
      gfx.fitToScreen({
        smooth: false,
      });
    }
  } catch (e) {
    logger.warn('failed to fitViewPort', e);
  }
}

function DocPeekPreviewEditor({
  xywh,
}: {
  xywh?: `[${number},${number},${number},${number}]`;
}) {
  const { editorService } = useServices({
    EditorService,
  });
  const editor = editorService.editor;
  const doc = editor.doc;
  const workspace = editor.doc.workspace;
  const mode = useLiveData(editor.mode$);
  const defaultOpenProperty = useLiveData(editor.defaultOpenProperty$);
  const workbench = useService(WorkbenchService).workbench;
  const peekView = useService(PeekViewService).peekView;
  const editorElement = useLiveData(editor.editorContainer$);

  const isInTrash = useLiveData(doc.record.trash$);

  const handleOnEditorReady = useCallback(
    (editorContainer: AffineEditorContainer) => {
      const disposableGroup = new DisposableGroup();
      const refNodeSlots =
        editorContainer.std.getOptional(RefNodeSlotsProvider);
      if (!refNodeSlots) return;
      // doc change event inside peek view should be handled by peek view
      disposableGroup.add(
        refNodeSlots.docLinkClicked.subscribe(
          ({ pageId, params, openMode, event, host }) => {
            if (host !== editorContainer.host) {
              return;
            }
            openMode ??=
              event && isNewTabTrigger(event)
                ? 'open-in-new-tab'
                : 'open-in-center-peek';

            if (openMode !== 'open-in-center-peek') {
              const at = (() => {
                if (openMode === 'open-in-active-view')
                  return 'active' as const;
                if (openMode === 'open-in-new-view')
                  return BUILD_CONFIG.isElectron
                    ? ('tail' as const)
                    : ('new-tab' as const);
                if (openMode === 'open-in-new-tab') return 'new-tab' as const;
                return 'active' as const;
              })();
              workbench.openDoc(
                {
                  docId: pageId,
                  mode: params?.mode,
                  blockIds: params?.blockIds,
                  elementIds: params?.elementIds,
                  refreshKey: nanoid(),
                },
                { at, show: true }
              );
              peekView.close(false);
            } else {
              peekView
                .open({
                  docRef: { docId: pageId, ...params },
                })
                .catch(console.error);
            }
          }
        )
      );

      const unbind = editor.bindEditorContainer(editorContainer);

      if (mode === 'edgeless') {
        fitViewport(editorContainer, xywh);
      }

      // Ensure title gets focus after the editor fully renders.
      // bindEditorContainer tries to focus the title, but the doc-title
      // element may not be in the DOM yet when it runs inside the peek view.
      const focusTimer = setTimeout(() => {
        const docTitle = editorContainer.querySelector('doc-title');
        const title = docTitle?.querySelector<
          HTMLElement & { inlineEditor: { focusEnd: () => void } | null }
        >('rich-text');
        title?.inlineEditor?.focusEnd();
      }, 100);

      // Tab/Shift+Tab navigation between title and body in peek view
      const handleTitleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          const host = editorContainer.host;
          const root = host?.store.root;
          if (!root || !host) return;

          const noteBlock = root.children[0];
          // Find the last block that has actual text content
          // (skip trailing empty paragraphs like the "Type '/' for commands" placeholder)
          const children = noteBlock?.children ?? [];
          let lastChild = null;
          for (let i = children.length - 1; i >= 0; i--) {
            if (
              children[i]?.text &&
              children[i].text?.length &&
              children[i].text.length > 0
            ) {
              lastChild = children[i];
              break;
            }
          }

          if (lastChild) {
            // Existing content: focus end of last block.
            // Use requestAnimationFrame so DOM focus has left the title first.
            requestAnimationFrame(() => {
              const blockComponent = host.view.getBlock(lastChild.id);
              const richText = blockComponent?.querySelector<
                HTMLElement & {
                  inlineEditor: { focusEnd: () => void } | null;
                }
              >('rich-text');
              if (richText?.inlineEditor) {
                richText.inlineEditor.focusEnd();
              } else {
                // Fallback: use selection API + host focus
                const textLen = lastChild.text?.length ?? 0;
                host.std.selection.setGroup('note', [
                  host.std.selection.create('text', {
                    from: {
                      blockId: lastChild.id,
                      index: textLen,
                      length: 0,
                    },
                    to: null,
                  }),
                ]);
                host.focus();
              }
            });
          } else {
            // Empty doc: create and focus first paragraph
            const rootComponent = host.view.getBlock(root.id);
            if (rootComponent && 'focusFirstParagraph' in rootComponent) {
              (rootComponent as any).focusFirstParagraph();
            }
          }
        }
      };

      const handleEditorKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && e.shiftKey) {
          // Only intercept when cursor is at the start of the first block
          const selection = editorContainer.host?.std.selection;
          const textSelection = selection?.find('text');
          if (!textSelection) return;

          const root = editorContainer.store.root;
          const noteBlock = root?.children[0];
          const firstBlock = noteBlock?.children[0];
          if (
            firstBlock &&
            textSelection.blockId === firstBlock.id &&
            textSelection.from.index === 0 &&
            textSelection.from.length === 0
          ) {
            e.preventDefault();
            const docTitle = editorContainer.querySelector('doc-title');
            const title = docTitle?.querySelector<
              HTMLElement & { inlineEditor: { focusEnd: () => void } | null }
            >('rich-text');
            title?.inlineEditor?.focusEnd();
          }
        }
      };

      const docTitle = editorContainer.querySelector('doc-title');
      docTitle?.addEventListener(
        'keydown',
        handleTitleKeydown as EventListener
      );
      editorContainer.addEventListener(
        'keydown',
        handleEditorKeydown as EventListener
      );

      return () => {
        clearTimeout(focusTimer);
        unbind();
        docTitle?.removeEventListener(
          'keydown',
          handleTitleKeydown as EventListener
        );
        editorContainer.removeEventListener(
          'keydown',
          handleEditorKeydown as EventListener
        );
        disposableGroup.dispose();
      };
    },
    [editor, mode, peekView, workbench, xywh]
  );

  useEffect(() => {
    const disposables: Subscription[] = [];
    const openHandler = (params: AIChatParams | null) => {
      if (!params) {
        return;
      }
      if (doc) {
        workbench.openDoc(doc.id);
        peekView.close();
        // chat panel open is already handled in <DetailPageImpl />
      }
    };
    disposables.push(AIAppEvents.requestOpenWithChat.subscribe(openHandler));
    disposables.push(AIAppEvents.requestSendWithChat.subscribe(openHandler));
    return () => disposables.forEach(d => d.unsubscribe());
  }, [doc, peekView, workbench, workspace.id]);

  const openOutlinePanel = useCallback(() => {
    workbench.openDoc(doc.id);
    workbench.openSidebar();
    workbench.activeView$.value.activeSidebarTab('outline');
    peekView.close();
  }, [doc, peekView, workbench]);

  const canEdit = useGuard('Doc_Update', doc.id);

  const readonly = !canEdit || isInTrash;

  return (
    <AffineErrorBoundary>
      <Scrollable.Root>
        <Scrollable.Viewport
          className={clsx('affine-page-viewport', styles.affineDocViewport)}
        >
          <Suspense fallback={<PageDetailLoading />}>
            <BlockSuiteEditor
              className={styles.editor}
              mode={mode}
              page={doc.blockSuiteDoc}
              readonly={readonly}
              onEditorReady={handleOnEditorReady}
              defaultOpenProperty={defaultOpenProperty}
            />
          </Suspense>
        </Scrollable.Viewport>
        <Scrollable.Scrollbar />
      </Scrollable.Root>
      {!BUILD_CONFIG.isMobileEdition && !BUILD_CONFIG.isMobileWeb ? (
        <EditorOutlineViewer
          editor={editorElement?.host ?? null}
          show={mode === 'page'}
          openOutlinePanel={openOutlinePanel}
        />
      ) : null}
    </AffineErrorBoundary>
  );
}

export function DocPeekPreview({
  docRef,
  animating,
}: {
  docRef: DocReferenceInfo;
  animating?: boolean;
}) {
  const {
    docId,
    blockIds,
    elementIds,
    mode,
    xywh,
    databaseId,
    databaseDocId,
    databaseRowId,
  } = docRef;
  const { doc, editor, loading } = useEditor(
    docId,
    mode,
    {
      blockIds,
      elementIds,
    },
    databaseId && databaseRowId && databaseDocId
      ? {
          docId: databaseDocId,
          databaseId,
          databaseRowId,
          type: 'database',
        }
      : undefined,
    !animating
  );

  const canAccess = useGuard('Doc_Read', docId);

  // if sync engine has been synced and the page is null, show 404 page.
  if (!doc || !editor || !canAccess) {
    return loading || canAccess === undefined ? (
      <PageDetailLoading key="current-page-is-null" />
    ) : (
      <PageNotFound noPermission />
    );
  }

  return (
    <FrameworkScope scope={doc.scope}>
      <FrameworkScope scope={editor.scope}>
        <DocPeekPreviewEditor xywh={xywh} />
      </FrameworkScope>
    </FrameworkScope>
  );
}
